/**
 * Orchestrates single-shot record + batch run against GlobeCanvas.
 */

import { useCallback, useRef, type RefObject } from "react";
import { toast } from "sonner";
import type { GlobePoint } from "#shared/schemas/globe.ts";
import type { GlobeCanvasHandle } from "@web/features/globe/GlobeCanvas.tsx";
import {
  loadDirectoryHandle,
  pickOutputDirectory,
  saveShotBlob,
  supportsDirectoryPicker,
} from "@web/features/globe/shotSave.ts";
import {
  dedupeTargetsByCountry,
  shotFilename,
  type ShotTarget,
} from "@web/features/globe/shotTargets.ts";
import { useGlobeShotStore } from "@web/stores/globeShot.ts";
import { useGlobeStore } from "@web/stores/globe.ts";

type FocusKind =
  | { kind: "country"; code: string }
  | { kind: "artist"; id: string };

/**
 * Must run in the same user-gesture stack as the click (before long awaits).
 * showDirectoryPicker / requestPermission fail after the flight.
 */
async function ensureDirectoryForGesture(opts: {
  /** When false (e.g. pendingRecord from navigation), never open the picker. */
  allowPicker: boolean;
}): Promise<FileSystemDirectoryHandle | null> {
  const existing = await loadDirectoryHandle();
  if (existing) {
    try {
      const query = existing.queryPermission?.bind(existing);
      const request = existing.requestPermission?.bind(existing);
      if (query && request) {
        const q = await query({ mode: "readwrite" });
        if (q === "granted") return existing;
        if (opts.allowPicker) {
          const r = await request({ mode: "readwrite" });
          if (r === "granted") return existing;
        }
      } else {
        return existing;
      }
    } catch {
      // Stale handle.
    }
  }
  if (!opts.allowPicker || !supportsDirectoryPicker()) return null;
  return pickOutputDirectory();
}

export function useShotSession(
  canvasRef: RefObject<GlobeCanvasHandle | null>,
  resolvePoint: (target: FocusKind) => GlobePoint | null,
) {
  const abortRef = useRef<AbortController | null>(null);
  const prevCleanRef = useRef(false);
  const pauseGate = useRef<{
    paused: boolean;
    waiters: Array<() => void>;
  }>({ paused: false, waiters: [] });
  /** Directory resolved under a user gesture; reused for the run. */
  const dirRef = useRef<FileSystemDirectoryHandle | null>(null);

  const enterCleanStage = useCallback(() => {
    prevCleanRef.current = useGlobeStore.getState().layers.cleanStage;
    useGlobeStore.getState().setLayer("cleanStage", true);
    useGlobeShotStore.getState().setRecording(true);
  }, []);

  const exitCleanStage = useCallback(() => {
    useGlobeStore.getState().setLayer("cleanStage", prevCleanRef.current);
    useGlobeShotStore.getState().setRecording(false);
  }, []);

  const recordOne = useCallback(
    async (
      point: GlobePoint,
      targetMeta: { artistName: string; countryLabel: string; countryCode: string },
      signal?: AbortSignal,
      opts?: { manageStage?: boolean },
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) throw new Error("Globe not ready");
      const shot = useGlobeShotStore.getState();
      const preset = shot.activePreset();
      const aspect = shot.aspect;
      const manageStage = opts?.manageStage ?? true;
      const blob = await canvas.recordShot(
        point,
        preset.values,
        aspect,
        {
          enterCleanStage: manageStage ? enterCleanStage : () => undefined,
          exitCleanStage: manageStage ? exitCleanStage : () => undefined,
        },
        signal,
      );
      const dir = dirRef.current;
      if (dir) {
        useGlobeShotStore.getState().setOutputFolderName(dir.name);
      }
      const filename = shotFilename(
        {
          id: point.id,
          artistId: targetMeta.artistName,
          artistName: targetMeta.artistName,
          countryCode: targetMeta.countryCode,
          countryLabel: targetMeta.countryLabel,
          lat: point.lat,
          lng: point.lng,
        },
        preset.name,
        blob.type,
      );
      // Never call showDirectoryPicker here — gesture is gone after the flight.
      const saved = await saveShotBlob(blob, filename, dir);
      return { filename, saved };
    },
    [canvasRef, enterCleanStage, exitCleanStage],
  );

  const previewCurrent = useCallback(
    async (point: GlobePoint | null) => {
      if (!point) {
        toast.message("No target", {
          description: "Select or focus a country first.",
        });
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas || canvas.isBusy()) return;
      const shot = useGlobeShotStore.getState();
      await canvas.previewShot(point, shot.activeValues(), shot.aspect);
      useGlobeStore.getState().setFocusedPointId(point.id);
      useGlobeStore.getState().setFocusSuspendAutoRotate(true);
    },
    [canvasRef],
  );

  const recordCurrent = useCallback(
    async (
      point: GlobePoint | null,
      artistName?: string,
      opts?: { allowPicker?: boolean },
    ) => {
      if (!point || !point.countryCode) {
        toast.message("No target", {
          description: "Select or focus a country first.",
        });
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas || canvas.isBusy()) return;

      const allowPicker = opts?.allowPicker ?? true;
      // Folder must be chosen in this gesture, before any long await.
      dirRef.current = await ensureDirectoryForGesture({ allowPicker });
      if (dirRef.current) {
        useGlobeShotStore.getState().setOutputFolderName(dirRef.current.name);
      } else if (allowPicker && supportsDirectoryPicker()) {
        toast.message("No output folder", {
          description: "Pick a folder (or cancel to download instead).",
        });
        // Still allow download fallback — user cancelled picker.
      }

      try {
        const { filename, saved } = await recordOne(point, {
          artistName: artistName ?? point.label,
          countryLabel: point.label,
          countryCode: point.countryCode,
        });
        toast.success(`Saved ${filename}`, {
          description:
            saved.mode === "directory"
              ? `Folder: ${saved.name}`
              : "Downloaded — choose an output folder first for direct saves.",
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const msg = err instanceof Error ? err.message : "Recording failed";
        toast.error(msg);
      }
    },
    [canvasRef, recordOne],
  );

  const waitIfPaused = useCallback(async () => {
    const gate = pauseGate.current;
    if (!gate.paused) return;
    await new Promise<void>((resolve) => {
      gate.waiters.push(resolve);
    });
  }, []);

  const runBatch = useCallback(async () => {
    const store = useGlobeShotStore.getState();
    if (store.batchStatus === "paused") {
      pauseGate.current.paused = false;
      const waiters = pauseGate.current.waiters.splice(0);
      for (const w of waiters) w();
      store.setBatchStatus("running");
      return;
    }

    let queue = [...store.queue];
    if (store.dedupeByCountry) {
      const { kept, skipped } = dedupeTargetsByCountry(queue);
      queue = kept;
      store.setQueue(kept);
      if (skipped.length) store.addTargets([], skipped);
    }

    if (queue.length === 0) {
      toast.message("Queue is empty");
      return;
    }

    // Gesture-safe: pick folder on Run click before the loop.
    dirRef.current = await ensureDirectoryForGesture({ allowPicker: true });
    if (dirRef.current) {
      store.setOutputFolderName(dirRef.current.name);
    } else if (supportsDirectoryPicker()) {
      toast.message("Saving via download", {
        description: "Choose an output folder next time for direct saves.",
      });
    }

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    pauseGate.current.paused = false;
    pauseGate.current.waiters = [];

    store.setBatchStatus("running");
    store.setBatchSummary(null);
    let recorded = 0;
    const presetName = store.activePreset().name;
    let outputLabel = store.outputFolderName ?? "downloads";

    enterCleanStage();
    try {
      for (let i = 0; i < queue.length; i++) {
        if (ac.signal.aborted) break;
        await waitIfPaused();
        if (ac.signal.aborted) break;

        const t = queue[i]!;
        useGlobeShotStore.getState().setBatchProgress({
          index: i + 1,
          total: queue.length,
          artistName: t.artistName,
          countryLabel: t.countryLabel,
        });

        const point = resolvePoint({ kind: "artist", id: t.artistId });
        if (!point) {
          useGlobeShotStore.getState().addTargets(
            [],
            [
              {
                artistId: t.artistId,
                artistName: t.artistName,
                reason: "Country point missing",
              },
            ],
          );
          continue;
        }

        useGlobeStore.getState().setFocusedPointId(point.id);
        const result = await recordOne(
          point,
          {
            artistName: t.artistName,
            countryLabel: t.countryLabel,
            countryCode: t.countryCode,
          },
          ac.signal,
          { manageStage: false },
        );
        recorded += 1;
        if (result.saved.mode === "directory") {
          outputLabel = result.saved.name;
        }
      }
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        const msg = err instanceof Error ? err.message : "Batch failed";
        toast.error(msg);
      }
    } finally {
      exitCleanStage();
      const skipped = useGlobeShotStore.getState().skipped.length;
      useGlobeShotStore.getState().setBatchStatus("done");
      useGlobeShotStore.getState().setBatchProgress(null);
      useGlobeShotStore.getState().setBatchSummary({
        recorded,
        skipped,
        outputLabel,
        presetName,
      });
      toast.success(`Batch done: ${recorded} recorded, ${skipped} skipped`, {
        description: `Preset ${presetName} · ${outputLabel}`,
      });
    }
  }, [enterCleanStage, exitCleanStage, recordOne, resolvePoint, waitIfPaused]);

  const pauseBatch = useCallback(() => {
    pauseGate.current.paused = true;
    useGlobeShotStore.getState().setBatchStatus("paused");
  }, []);

  const stopBatch = useCallback(() => {
    pauseGate.current.paused = false;
    const waiters = pauseGate.current.waiters.splice(0);
    for (const w of waiters) w();
    abortRef.current?.abort();
  }, []);

  const recordFromTarget = useCallback(
    async (target: ShotTarget) => {
      const point = resolvePoint({ kind: "artist", id: target.artistId });
      if (!point) {
        toast.message("No country data", {
          description: `${target.artistName} has no globe point.`,
        });
        return;
      }
      await recordCurrent(point, target.artistName);
    },
    [resolvePoint, recordCurrent],
  );

  return {
    previewCurrent,
    recordCurrent,
    recordFromTarget,
    runBatch,
    pauseBatch,
    stopBatch,
  };
}
